import { appSessionStorage } from '../services/index';

// Splits a Magento street line into [street, housenumber]. Customer
// address-book entries can hold "street housenumber" in a single line.
const splitStreetLine = (street: string[]): [string, string] => {
  let streetName = street[0] || '';
  let housenumber = street[1] || '';
  if (!housenumber && streetName) {
    const match = streetName.match(/^(.*?)[\s,]+(\d+[a-zA-Z\-/]*)$/);
    if (match) {
      streetName = match[1];
      housenumber = match[2];
    }
  }
  return [streetName, housenumber];
};

const POSTNL_ADDRESS_INCOMPLETE =
  'Enter a valid postcode and house number first so we can show the delivery options.';
const POSTNL_GENERIC_ERROR =
  'Delivery options could not be loaded. Please check your address and try again.';

/**
 * Map technical PostNL/Magento API errors to customer-friendly i18n keys.
 */
const mapPostnlError = (error: unknown): string => {
  const message = String(error || '').trim();
  if (!message) {
    return POSTNL_GENERIC_ERROR;
  }

  // Already a friendly key used by this store.
  if (
    message === POSTNL_ADDRESS_INCOMPLETE ||
    message === POSTNL_GENERIC_ERROR ||
    message === 'Address incomplete'
  ) {
    return POSTNL_ADDRESS_INCOMPLETE;
  }

  const normalized = message.toLowerCase();
  if (
    normalized.includes('postnl-0124') ||
    normalized.includes('unable to extract the house number') ||
    normalized.includes('street data could not be found') ||
    normalized.includes('housenumber') ||
    normalized.includes('house number') ||
    normalized.includes('postcode') ||
    normalized.includes('address incomplete')
  ) {
    return POSTNL_ADDRESS_INCOMPLETE;
  }

  // Hide raw PostNL error codes (e.g. POSTNL-0124 : …) from customers.
  if (/^postnl-\d+/i.test(message)) {
    return POSTNL_ADDRESS_INCOMPLETE;
  }

  return POSTNL_GENERIC_ERROR;
};

const initialState = () => {
  return {
    shippingMethod: appSessionStorage.getItem('checkout-shipping-selected-method', {}),
    shippingMethods: [],
    shippingCountries: {},
    loadingShippingMethods: false,
    postnlTimeframes: [],
    postnlDeliveryOption: appSessionStorage.getItem('checkout-postnl-delivery-option', null),
    loadingPostnlTimeframes: false,
    postnlError: null,
    postnlMode: appSessionStorage.getItem('checkout-postnl-mode', 'delivery'),
    postnlPickupLocations: [],
    postnlPickupDate: null,
    postnlPickupPrice: null,
    loadingPostnlLocations: false,
    postnlEveningFee: 0,
  };
};

const getters = {
  shippingMethod: (state) => state.shippingMethod,
  shippingMethods: (state) => state.shippingMethods,
  shippingCountries: (state) => state.shippingCountries,
  loadingShippingMethods: (state) => state.loadingShippingMethods,
  postnlTimeframes: (state) => state.postnlTimeframes,
  postnlDeliveryOption: (state) => state.postnlDeliveryOption,
  loadingPostnlTimeframes: (state) => state.loadingPostnlTimeframes,
  postnlError: (state) => state.postnlError,
  postnlMode: (state) => state.postnlMode,
  postnlPickupLocations: (state) => state.postnlPickupLocations,
  postnlPickupDate: (state) => state.postnlPickupDate,
  postnlPickupPrice: (state) => state.postnlPickupPrice,
  loadingPostnlLocations: (state) => state.loadingPostnlLocations,
  postnlEveningFee: (state) => state.postnlEveningFee,
  isPostnlShippingSelected: (state) => {
    const carrier = state.shippingMethod?.carrier_code || '';
    return carrier === 'tig_postnl' || carrier.startsWith('tig_postnl');
  },
};

const mutations = {
  SET_SHIPPING_METHOD: (state, payload) => (state.shippingMethod = payload),
  SET_SHIPPING_METHODS: (state, payload) => (state.shippingMethods = payload),
  SET_SHIPPING_COUNTRIES: (state, payload) => (state.shippingCountries = payload),
  SET_LOADING_SHIPPING_METHODS: (state, payload) => (state.loadingShippingMethods = payload),
  SET_POSTNL_TIMEFRAMES: (state, payload) => (state.postnlTimeframes = payload),
  SET_POSTNL_DELIVERY_OPTION: (state, payload) => {
    state.postnlDeliveryOption = payload;
    if (payload) {
      appSessionStorage.setItem('checkout-postnl-delivery-option', payload);
    } else {
      appSessionStorage.removeItem('checkout-postnl-delivery-option');
    }
  },
  SET_LOADING_POSTNL_TIMEFRAMES: (state, payload) => (state.loadingPostnlTimeframes = payload),
  SET_POSTNL_ERROR: (state, payload) => (state.postnlError = payload),
  SET_POSTNL_MODE: (state, payload) => {
    state.postnlMode = payload;
    appSessionStorage.setItem('checkout-postnl-mode', payload);
  },
  SET_POSTNL_PICKUP_LOCATIONS: (state, payload) => (state.postnlPickupLocations = payload),
  SET_POSTNL_PICKUP_DATE: (state, payload) => (state.postnlPickupDate = payload),
  SET_POSTNL_PICKUP_PRICE: (state, payload) => (state.postnlPickupPrice = payload),
  SET_LOADING_POSTNL_LOCATIONS: (state, payload) => (state.loadingPostnlLocations = payload),
  SET_POSTNL_EVENING_FEE: (state, payload) => (state.postnlEveningFee = payload),
};

const actions = {
  resetForAddressChange({ commit }) {
    commit('SET_SHIPPING_METHODS', []);
    commit('SET_SHIPPING_METHOD', {});
    appSessionStorage.removeItem('checkout-shipping-selected-method');
    commit('SET_POSTNL_TIMEFRAMES', []);
    commit('SET_POSTNL_PICKUP_LOCATIONS', []);
    commit('SET_POSTNL_DELIVERY_OPTION', null);
    commit('SET_POSTNL_ERROR', null);
  },
  async setShippingMethod({ commit, dispatch }, shippingMethod) {
    commit('SET_SHIPPING_METHOD', shippingMethod);
    appSessionStorage.setItem('checkout-shipping-selected-method', shippingMethod);

    const carrier = shippingMethod?.carrier_code || '';
    if (carrier === 'tig_postnl' || carrier.startsWith('tig_postnl')) {
      await dispatch('refreshPostnlOptions');
    } else {
      commit('SET_POSTNL_TIMEFRAMES', []);
      commit('SET_POSTNL_PICKUP_LOCATIONS', []);
      commit('SET_POSTNL_DELIVERY_OPTION', null);
      commit('SET_POSTNL_ERROR', null);
    }
  },
  async collectShippingMethods({ rootGetters, commit, dispatch, state }) {
    try {
      const [_billingAddress, _shippingAddress] = rootGetters['CheckoutAddress/getAddressData'];

      // Without a usable shipping address the API rejects the call; wait until
      // the address step has produced at least a postcode.
      if (!_shippingAddress || !_shippingAddress['postcode']) {
        await dispatch('resetForAddressChange');
        return;
      }

      commit('SET_LOADING_SHIPPING_METHODS', true);
      const { data } = await this.$solarClient.post('/api/checkout/shipping/collectShippingMethods', {
        address: _shippingAddress,
      });

      commit('SET_SHIPPING_METHODS', data);

      if (data.length === 1) {
        await dispatch('setShippingMethod', data[0]);
      } else if ((state.shippingMethod?.carrier_code || '').startsWith('tig_postnl')) {
        await dispatch('refreshPostnlOptions');
      }
    } catch (err) {
      //
    } finally {
      commit('SET_LOADING_SHIPPING_METHODS', false);
    }
  },
  /** Reload PostNL UI data for the active mode (delivery dates vs pickup points). */
  async refreshPostnlOptions({ state, dispatch }) {
    if (state.postnlMode === 'pickup') {
      return dispatch('collectPostnlPickupLocations');
    }

    return dispatch('collectPostnlTimeframes');
  },
  async collectPostnlTimeframes({ rootGetters, commit, state }) {
    const [_billingAddress, _shippingAddress] = rootGetters['CheckoutAddress/getAddressData'];
    const street = Array.isArray(_shippingAddress.street) ? _shippingAddress.street : ['', '', ''];

    const [streetName, housenumber] = splitStreetLine(street);

    const address = {
      country: _shippingAddress.country_id || _shippingAddress.country || 'NL',
      country_id: _shippingAddress.country_id || _shippingAddress.country || 'NL',
      postcode: (_shippingAddress.postcode || '').replace(/\s+/g, ''),
      street: streetName,
      housenumber,
      city: _shippingAddress.city || '',
    };

    if (!address.postcode || !address.housenumber || !address.street) {
      commit('SET_POSTNL_ERROR', POSTNL_ADDRESS_INCOMPLETE);
      commit('SET_POSTNL_TIMEFRAMES', []);
      return;
    }

    commit('SET_LOADING_POSTNL_TIMEFRAMES', true);
    commit('SET_POSTNL_ERROR', null);

    try {
      const { data } = await this.$solarClient.post('/api/checkout/shipping/postnl/timeframes', { address });
      const frames = Array.isArray(data?.timeframes) ? data.timeframes : [];
      commit('SET_POSTNL_TIMEFRAMES', frames);
      if (data?.evening_delivery_fee !== undefined && data?.evening_delivery_fee !== null) {
        commit('SET_POSTNL_EVENING_FEE', Number(data.evening_delivery_fee) || 0);
      }

      if (data?.error) {
        commit('SET_POSTNL_ERROR', mapPostnlError(data.error));
      }

      // Auto-select first daytime option when nothing chosen yet — never when in pickup mode.
      const first = frames.flat().find((frame) => frame?.date && !frame?.fallback);
      if (first && state.postnlMode !== 'pickup' && state.postnlDeliveryOption?.type !== 'pickup') {
        commit('SET_POSTNL_DELIVERY_OPTION', {
          type: 'delivery',
          date: first.date,
          option: first.option || 'Daytime',
          from: first.from,
          to: first.to,
          country: address.country,
          address,
        });
      }
    } catch (err) {
      commit('SET_POSTNL_ERROR', mapPostnlError(err?.response?.data?.message));
      commit('SET_POSTNL_TIMEFRAMES', []);
    } finally {
      commit('SET_LOADING_POSTNL_TIMEFRAMES', false);
    }
  },
  async savePostnlDeliveryOption({ state, commit }) {
    if (!state.postnlDeliveryOption) {
      return false;
    }

    try {
      await this.$solarClient.post('/api/checkout/shipping/postnl/delivery-options', {
        option: state.postnlDeliveryOption,
      });
      return true;
    } catch (err) {
      commit('SET_POSTNL_ERROR', mapPostnlError(err?.response?.data?.message || POSTNL_GENERIC_ERROR));
      return false;
    }
  },
  async collectPostnlPickupLocations({ rootGetters, commit }) {
    const [_billingAddress, _shippingAddress] = rootGetters['CheckoutAddress/getAddressData'];
    const street = Array.isArray(_shippingAddress.street) ? _shippingAddress.street : ['', '', ''];

    const [streetName, housenumber] = splitStreetLine(street);

    const address = {
      country: _shippingAddress.country_id || _shippingAddress.country || 'NL',
      country_id: _shippingAddress.country_id || _shippingAddress.country || 'NL',
      postcode: (_shippingAddress.postcode || '').replace(/\s+/g, ''),
      street: [streetName, housenumber, street[2] || ''],
      housenumber,
      city: _shippingAddress.city || '',
    };

    if (!address.postcode || !address.housenumber || !streetName) {
      commit('SET_POSTNL_ERROR', POSTNL_ADDRESS_INCOMPLETE);
      commit('SET_POSTNL_PICKUP_LOCATIONS', []);
      return;
    }

    commit('SET_LOADING_POSTNL_LOCATIONS', true);
    commit('SET_POSTNL_ERROR', null);

    try {
      const { data } = await this.$solarClient.post('/api/checkout/shipping/postnl/locations', { address });
      commit('SET_POSTNL_PICKUP_LOCATIONS', Array.isArray(data?.locations) ? data.locations : []);
      commit('SET_POSTNL_PICKUP_DATE', data?.pickup_date || null);
      commit('SET_POSTNL_PICKUP_PRICE', data?.price ?? null);

      if (data?.error) {
        commit('SET_POSTNL_ERROR', mapPostnlError(data.error));
      }
    } catch (err) {
      commit('SET_POSTNL_ERROR', mapPostnlError(err?.response?.data?.message));
      commit('SET_POSTNL_PICKUP_LOCATIONS', []);
    } finally {
      commit('SET_LOADING_POSTNL_LOCATIONS', false);
    }
  },
  setPostnlMode({ commit, dispatch }, mode) {
    commit('SET_POSTNL_MODE', mode);
    commit('SET_POSTNL_DELIVERY_OPTION', null);
    commit('SET_POSTNL_ERROR', null);

    if (mode === 'pickup') {
      return dispatch('collectPostnlPickupLocations');
    }

    return dispatch('collectPostnlTimeframes');
  },
  setPostnlDeliveryOption({ commit }, option) {
    commit('SET_POSTNL_DELIVERY_OPTION', option);
  },
  setShippingCountries({ commit }, payload) {
    try {
      commit(
        'SET_SHIPPING_COUNTRIES',
        payload.sort((a, b) => (a['full_name_locale'] > b['full_name_locale'] && 1) || -1)
      );
    } catch (err) {
      //
    }
  },
};

const state = initialState();

const CheckoutShipping = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};

export default CheckoutShipping;
